// Lo que dicen las tarjetas del asistente sobre este módulo, en los cuatro idiomas.
//
// Aparte del resto de los textos por lo mismo que la analítica: estas claves son
// un CONTRATO con `backend/assistant.js`, no copia de una pantalla. Cada frase
// que aquel fichero arma con `phrase('x.y', …)` se busca aquí como
// `quotes.assistantTools.x.y`; una que falte no rompe nada —la tarjeta enseña la
// frase en inglés que trae el backend—, pero sale en inglés en medio de una
// pantalla en castellano. `tests/unit/assistantTools.test.js` lo comprueba.
//
// Bajo `assistantTools` y no bajo `assistant`: `quotes.assistant` ya es el
// asistente del editor (la pestaña del presupuesto), y mezclar los dos árboles
// es la forma de que un día una clave pise a otra.

const assistantTools = {
  es: {
    where: { quote: 'Presupuesto' },
    field: {
      title: 'Título', customer: 'Cliente', job: 'El trabajo', toAdd: 'Qué añadir', shape: 'Cómo',
      totalNow: 'Total ahora', project: 'Obra o expediente', projectBefore: 'Ahora está en',
      ruleTitle: 'Nombre', category: 'Categoría', when: 'Cuándo se aplica', rule: 'Qué hay que hacer',
      consequence: 'Si no se hace', total: 'Total', note: 'Nota', to: 'Para', subject: 'Asunto', message: 'Mensaje', attached: 'Adjunto',
    },
    value: { assembly: 'Una línea con su desglose', lines: 'Varias líneas', none: 'Ninguno', attachPdf: 'El PDF del presupuesto' },
    note: {
      draftOnly: 'Se queda en borrador: no se manda a nadie.',
      titleGenerated: 'Sin título, se escribe uno a partir del trabajo.',
      appends: 'Se añade: no se sustituye nada de lo que ya tiene.',
      acceptedLocked: 'Este presupuesto está aceptado y no se puede editar: duplícalo en un borrador nuevo.',
      announced: 'El presupuesto está aceptado, así que la obra se entera ahora.',
      notYet: 'Aún no está aceptado: queda archivado y la obra se entera cuando se acepte.',
      everyQuote: 'Todos los presupuestos que se redacten a partir de ahora la aplicarán.',
      notSent: 'Solo se acepta un presupuesto enviado, y este está en {status}.',
      quoteNotFound: 'Ese presupuesto no está en el archivo.',
      leaves: 'Esto sale de la empresa: el cliente verá este precio.',
      ownAccount: 'Sale desde tu propia cuenta de correo, como «Enviar por email». Si no tienes, añádela antes en Correo.',
      noAddress: 'El presupuesto no tiene dirección: escribe una antes de enviarlo.',
      notSendable: 'Un presupuesto en {status} no se vuelve a enviar.',
      noLines: 'Un presupuesto sin líneas no se puede enviar.',
    },
    draft: {
      action: 'Redactar un presupuesto nuevo',
      summary: {
        customer: 'Redactar un presupuesto nuevo para {customer}: «{title}»',
        plain: 'Redactar un presupuesto nuevo: «{title}»',
      },
      outcome: 'Presupuesto {code} redactado con {lines} líneas',
      outcomeEmpty: 'No se ha redactado nada: la descripción no daba líneas que presupuestar',
    },
    addLines: {
      action: 'Añadir líneas a un presupuesto',
      summary: 'Añadir «{what}» a {code}',
      outcome: '{count} líneas añadidas a {code}; total {total}',
      outcomeEmpty: 'No se ha añadido nada: la descripción no daba líneas que presupuestar',
    },
    link: {
      action: 'Archivar un presupuesto en una obra',
      actionUnfile: 'Sacar un presupuesto de su obra',
      summary: {
        file: 'Archivar {code} en «{project}»',
        unfile: 'Sacar {code} de la obra en la que está',
      },
      outcome: '{code} archivado en {project}',
      outcomeUnfiled: '{code} ya no está archivado en ninguna obra',
    },
    rule: {
      action: 'Guardar un criterio para presupuestar',
      summary: 'Guardar el criterio «{title}»',
      outcome: 'Criterio «{title}» guardado',
    },
    accept: {
      action: 'Apuntar que el cliente ha aceptado',
      summary: 'Apuntar que {customer} ha aceptado {code} ({total})',
      after: 'Quedará cerrado para siempre y se avisará al resto del ERP.',
      outcome: '{code} marcado como aceptado',
    },
    send: {
      action: 'Enviar el presupuesto al cliente',
      summary: 'Enviar {code} ({total}) a {to}: sale de la empresa',
      after: 'Quedará como enviado y se guardará la copia que salió.',
      outcome: '{code} enviado a {to}',
    },
  },

  ca: {
    where: { quote: 'Pressupost' },
    field: {
      title: 'Títol', customer: 'Client', job: 'La feina', toAdd: 'Què cal afegir', shape: 'Com',
      totalNow: 'Total ara', project: 'Obra o expedient', projectBefore: 'Ara és a',
      ruleTitle: 'Nom', category: 'Categoria', when: 'Quan s\'aplica', rule: 'Què cal fer',
      consequence: 'Si no es fa', total: 'Total', note: 'Nota', to: 'Per a', subject: 'Assumpte', message: 'Missatge', attached: 'Adjunt',
    },
    value: { assembly: 'Una línia amb el seu desglossament', lines: 'Diverses línies', none: 'Cap', attachPdf: 'El PDF del pressupost' },
    note: {
      draftOnly: 'Es queda en esborrany: no s\'envia a ningú.',
      titleGenerated: 'Sense títol, se n\'escriu un a partir de la feina.',
      appends: 'S\'afegeix: no se substitueix res del que ja té.',
      acceptedLocked: 'Aquest pressupost està acceptat i no es pot editar: duplica\'l en un esborrany nou.',
      announced: 'El pressupost està acceptat, així que l\'obra se n\'assabenta ara.',
      notYet: 'Encara no està acceptat: queda arxivat i l\'obra se n\'assabenta quan s\'accepti.',
      everyQuote: 'Tots els pressupostos que es redactin a partir d\'ara l\'aplicaran.',
      notSent: 'Només s\'accepta un pressupost enviat, i aquest està en {status}.',
      quoteNotFound: 'Aquest pressupost no és a l\'arxiu.',
      leaves: 'Això surt de l\'empresa: el client veurà aquest preu.',
      ownAccount: 'Surt des del teu propi compte de correu, com «Enviar per correu». Si no en tens, afegeix-lo abans a Correu.',
      noAddress: 'El pressupost no té adreça: escriu-ne una abans d\'enviar-lo.',
      notSendable: 'Un pressupost en {status} no es torna a enviar.',
      noLines: 'Un pressupost sense línies no es pot enviar.',
    },
    draft: {
      action: 'Redactar un pressupost nou',
      summary: {
        customer: 'Redactar un pressupost nou per a {customer}: «{title}»',
        plain: 'Redactar un pressupost nou: «{title}»',
      },
      outcome: 'Pressupost {code} redactat amb {lines} línies',
      outcomeEmpty: 'No s\'ha redactat res: la descripció no donava línies per pressupostar',
    },
    addLines: {
      action: 'Afegir línies a un pressupost',
      summary: 'Afegir «{what}» a {code}',
      outcome: '{count} línies afegides a {code}; total {total}',
      outcomeEmpty: 'No s\'ha afegit res: la descripció no donava línies per pressupostar',
    },
    link: {
      action: 'Arxivar un pressupost en una obra',
      actionUnfile: 'Treure un pressupost de la seva obra',
      summary: {
        file: 'Arxivar {code} a «{project}»',
        unfile: 'Treure {code} de l\'obra on és',
      },
      outcome: '{code} arxivat a {project}',
      outcomeUnfiled: '{code} ja no és arxivat a cap obra',
    },
    rule: {
      action: 'Desar un criteri per pressupostar',
      summary: 'Desar el criteri «{title}»',
      outcome: 'Criteri «{title}» desat',
    },
    accept: {
      action: 'Apuntar que el client ha acceptat',
      summary: 'Apuntar que {customer} ha acceptat {code} ({total})',
      after: 'Quedarà tancat per sempre i s\'avisarà la resta de l\'ERP.',
      outcome: '{code} marcat com a acceptat',
    },
    send: {
      action: 'Enviar el pressupost al client',
      summary: 'Enviar {code} ({total}) a {to}: surt de l\'empresa',
      after: 'Quedarà com a enviat i es desarà la còpia que va sortir.',
      outcome: '{code} enviat a {to}',
    },
  },

  en: {
    where: { quote: 'Quote' },
    field: {
      title: 'Title', customer: 'Customer', job: 'The job', toAdd: 'What to add', shape: 'As',
      totalNow: 'Total now', project: 'Job', projectBefore: 'Filed under now',
      ruleTitle: 'Name', category: 'Category', when: 'When it applies', rule: 'What to do',
      consequence: 'If it is skipped', total: 'Total', note: 'Note', to: 'To', subject: 'Subject', message: 'Message', attached: 'Attached',
    },
    value: { assembly: 'One line with its components', lines: 'Separate lines', none: 'None', attachPdf: 'The quote\'s PDF' },
    note: {
      draftOnly: 'It stays a draft: nothing is sent to anybody.',
      titleGenerated: 'Without a title, one is written from the job.',
      appends: 'It appends: nothing already on the quote is replaced.',
      acceptedLocked: 'This quote is accepted and cannot be edited: duplicate it into a new draft.',
      announced: 'The quote is accepted, so the job hears about it now.',
      notYet: 'The quote is not accepted yet: the link is kept and the job hears about it on acceptance.',
      everyQuote: 'Every quote drafted from now on applies it.',
      notSent: 'Only a sent quote can be accepted, and this one is {status}.',
      quoteNotFound: 'That quote is not on file.',
      leaves: 'This leaves the company: the customer will see this price.',
      ownAccount: 'It goes from your own mail account, like «Send by email». Without one, add it in Mail first.',
      noAddress: 'The quote has no address: write one before sending.',
      notSendable: 'A quote that is {status} is not sent again.',
      noLines: 'A quote with no lines cannot be sent.',
    },
    draft: {
      action: 'Draft a new quote',
      summary: {
        customer: 'Draft a new quote for {customer}: “{title}”',
        plain: 'Draft a new quote: “{title}”',
      },
      outcome: 'Quote {code} drafted with {lines} lines',
      outcomeEmpty: 'Nothing was drafted: the description gave no lines to price',
    },
    addLines: {
      action: 'Add lines to a quote',
      summary: 'Add “{what}” to {code}',
      outcome: '{count} lines added to {code}; total {total}',
      outcomeEmpty: 'Nothing was added: the description gave no lines to price',
    },
    link: {
      action: 'File a quote under a job',
      actionUnfile: 'Take a quote off its job',
      summary: {
        file: 'File {code} under “{project}”',
        unfile: 'Take {code} off the job it is filed under',
      },
      outcome: '{code} filed under {project}',
      outcomeUnfiled: '{code} is no longer filed under a job',
    },
    rule: {
      action: 'Save a quoting rule',
      summary: 'Save the rule “{title}”',
      outcome: 'Rule “{title}” saved',
    },
    accept: {
      action: 'Record that the customer accepted',
      summary: 'Record that {customer} accepted {code} ({total})',
      after: 'It will be locked for good, and the rest of the ERP will be told.',
      outcome: '{code} marked accepted',
    },
    send: {
      action: 'Send the quote to the customer',
      summary: 'Email {code} ({total}) to {to}: it leaves the company',
      after: 'It will be marked as sent and the copy that went out will be filed.',
      outcome: '{code} sent to {to}',
    },
  },

  fr: {
    where: { quote: 'Devis' },
    field: {
      title: 'Titre', customer: 'Client', job: 'Le travail', toAdd: 'Ce qu’il faut ajouter', shape: 'Sous forme de',
      totalNow: 'Total actuel', project: 'Chantier ou dossier', projectBefore: 'Classé actuellement sous',
      ruleTitle: 'Nom', category: 'Catégorie', when: 'Quand elle s’applique', rule: 'Ce qu’il faut faire',
      consequence: 'Si on l’oublie', total: 'Total', note: 'Note', to: 'À', subject: 'Objet', message: 'Message', attached: 'Pièce jointe',
    },
    value: { assembly: 'Une ligne avec son détail', lines: 'Plusieurs lignes', none: 'Aucun', attachPdf: 'Le PDF du devis' },
    note: {
      draftOnly: 'Il reste en brouillon : rien n’est envoyé à personne.',
      titleGenerated: 'Sans titre, un titre est rédigé à partir du travail.',
      appends: 'C’est un ajout : rien de ce qui est déjà sur le devis n’est remplacé.',
      acceptedLocked: 'Ce devis est accepté et ne peut plus être modifié : dupliquez-le en un nouveau brouillon.',
      announced: 'Le devis est accepté : le chantier en est informé maintenant.',
      notYet: 'Le devis n’est pas encore accepté : le lien est gardé et le chantier en sera informé à l’acceptation.',
      everyQuote: 'Tous les devis rédigés à partir de maintenant l’appliqueront.',
      notSent: 'Seul un devis envoyé peut être accepté, et celui-ci est {status}.',
      quoteNotFound: 'Ce devis n’existe pas.',
      leaves: 'Cela sort de l’entreprise : le client verra ce prix.',
      ownAccount: 'Il part de votre propre compte de messagerie, comme « Envoyer par e-mail ». Sans compte, ajoutez-le d’abord dans Messagerie.',
      noAddress: 'Le devis n’a pas d’adresse : saisissez-en une avant de l’envoyer.',
      notSendable: 'Un devis {status} n’est pas renvoyé.',
      noLines: 'Un devis sans lignes ne peut pas être envoyé.',
    },
    draft: {
      action: 'Rédiger un nouveau devis',
      summary: {
        customer: 'Rédiger un nouveau devis pour {customer} : « {title} »',
        plain: 'Rédiger un nouveau devis : « {title} »',
      },
      outcome: 'Devis {code} rédigé avec {lines} lignes',
      outcomeEmpty: 'Rien n’a été rédigé : la description ne donnait aucune ligne à chiffrer',
    },
    addLines: {
      action: 'Ajouter des lignes à un devis',
      summary: 'Ajouter « {what} » à {code}',
      outcome: '{count} lignes ajoutées à {code} ; total {total}',
      outcomeEmpty: 'Rien n’a été ajouté : la description ne donnait aucune ligne à chiffrer',
    },
    link: {
      action: 'Classer un devis sous un chantier',
      actionUnfile: 'Retirer un devis de son chantier',
      summary: {
        file: 'Classer {code} sous « {project} »',
        unfile: 'Retirer {code} du chantier sous lequel il est classé',
      },
      outcome: '{code} classé sous {project}',
      outcomeUnfiled: '{code} n’est plus classé sous aucun chantier',
    },
    rule: {
      action: 'Enregistrer un critère de chiffrage',
      summary: 'Enregistrer le critère « {title} »',
      outcome: 'Critère « {title} » enregistré',
    },
    accept: {
      action: 'Noter que le client a accepté',
      summary: 'Noter que {customer} a accepté {code} ({total})',
      after: 'Il sera verrouillé définitivement et le reste de l’ERP en sera informé.',
      outcome: '{code} marqué comme accepté',
    },
    send: {
      action: 'Envoyer le devis au client',
      summary: 'Envoyer {code} ({total}) à {to} : cela sort de l’entreprise',
      after: 'Il sera marqué comme envoyé et la copie partie sera classée.',
      outcome: '{code} envoyé à {to}',
    },
  },
}

/** Los textos del módulo, con los de las tarjetas del asistente colgados de `<namespace>.assistantTools`. */
export function withAssistantTools(base, namespace) {
  const merged = {}
  for (const [locale, catalogue] of Object.entries(base)) {
    merged[locale] = {
      ...catalogue,
      [namespace]: { ...catalogue[namespace], assistantTools: assistantTools[locale] },
    }
  }
  return merged
}

export default assistantTools
